<template>
  <DsCard variant="outlined" padding="md" class="w-full">
    <h4 class="text-[1rem] leading-[1.5] text-primary font-inter font-semibold mb-4">
      {{ cardTitle }}
    </h4>
    <div class="w-full flex flex-col gap-4">
      <Vueform
        :key="formKey"
        class="w-full"
        :float-placeholders="false"
        :display-errors="false"
        :force-labels="false"
        :display-messages="false"
        v-model="formData"
        ref="formRef"
      >
        <TextElement
          class="locked-field"
          name="cgName"
          v-model="formData.cgName"
          :default="formData.cgName"
          label="Connect Group"
          placeholder="None"
          :rules="['required', invalidGroup]"
          :messages="{
            required: 'Required',
            invalidGroup: 'Enter a valid Connect Group',
          }"
          :disabled="fieldDisable"
        />
      </Vueform>
      <div v-if="isRequested" class="w-full flex gap-[8px]">
        <DsButton variant="borderless" size="48" class="flex-1" @click="onRequestCancel"
          >Cancel</DsButton
        >
        <DsButton
          variant="bordered"
          shape="pill"
          size="48"
          class="flex-1"
          :loading="loading"
          :disabled="disabledSubmit"
          @click="handleSubmit"
          >Submit</DsButton
        >
      </div>
      <DsButton v-else variant="bordered" shape="pill" size="48" class="w-full" @click="onRequest">
        {{ requestText }}
        <DsIcon name="edit" size="sm" class="ms-1" />
      </DsButton>
    </div>
  </DsCard>
</template>
<script>
import { VueForm, Validator } from '@vueform/vueform';
import { mapState } from 'vuex';
import axios from 'axios';
import { ERROR_GROUP } from '@/constants/validation/error-messages';
import { resolveCgId } from '@/utils/cg-status';
import { cgUpdatedTitle } from '@/utils/member-split/unlinked-profile-copy';

const DEFAULT_TITLE = 'Request for an update if these fields are incorrect.';
const MEMBER_RECEIVED_TITLE = 'Thanks! We’ve received your request and will be in touch soon ✅';

// The field passes only when a request can actually be sent: the same lookup
// onRequestSubmit resolves the id with. Both sides are normalised: a stripped
// input never equalled a spaced key.
function isResolvableCg(value, cgData) {
  if (!value) return false;
  if (!cgData) return true;
  return resolveCgId(value, cgData) !== undefined;
}
import {
  Card as DsCard,
  Button as DsButton,
  Icon as DsIcon,
} from '@team-hogc/hogc-one-design-system';

export default {
  components: {
    VueForm,
    DsCard,
    DsButton,
    DsIcon,
  },
  props: {
    payload: {
      type: Object,
      default: {},
    },
    cgData: {
      type: Object,
      default: {},
    },
    showError: {
      type: Object,
      default: {},
    },
  },
  computed: {
    ...mapState('user', ['token']),
    hasNoMember() {
      return this.$store.state.profile?.hasNoMember === true;
    },
    // Decided from the value itself, not from Vueform's `errors`: those refresh
    // only after Vueform re-validates, so reading them as the model changes
    // judged every keystroke by the previous one, and Submit stayed disabled for a
    // login that started with no CG and typed a valid one.
    disabledSubmit() {
      return !isResolvableCg(this.formData.cgName, this.cgData);
    },
  },
  emits: ['hideFooter', 'profileRefreshed'],
  mounted() {
    if (Object.keys(this.payload).length > 0) {
      this.updatedPayload = JSON.parse(JSON.stringify(this.payload));
      this.formData.cgName = this.payload.cgName || '';
      this.cardTitle = this.restingTitle(this.payload);
    }
  },
  data() {
    return {
      requestText: 'Request for an update',
      isRequested: false,
      fieldDisable: true,
      cardTitle: DEFAULT_TITLE,
      // Set once a login waiting for its link has moved its CG: the card keeps saying
      // so when the page hands it the refreshed payload.
      updatedTitle: '',
      updatedPayload: {},
      formKey: 0,
      loading: false,
      formData: {
        cgName: '',
      },
    };
  },
  methods: {
    restingTitle(payload) {
      if (this.updatedTitle) return this.updatedTitle;
      return payload.cgUpdateRequestFormId ? MEMBER_RECEIVED_TITLE : DEFAULT_TITLE;
    },
    onRequest() {
      this.isRequested = !this.isRequested;
      this.fieldDisable = !this.fieldDisable;
      if (this.fieldDisable) {
        this.cardTitle = DEFAULT_TITLE;
      } else {
        this.cardTitle = 'Which connect group (CG) do you belong to?';
      }
    },
    async handleSubmit() {
      // Validate form before submitting
      if (this.$refs.formRef) {
        await this.$refs.formRef.validate();
      }
      if (this.disabledSubmit) return;
      try {
        await this.onRequestSubmit(); // Call the function if no errors
      } catch (e) {
        console.error('CG update request error:', e);
        this.$store.dispatch('toast/error', 'Failed to submit request. Please try again.');
      }
    },
    async onRequestSubmit() {
      // Normalize CG name: remove whitespace and convert to uppercase
      const normalizedCgName = this.formData.cgName.toUpperCase().replace(/\s+/g, '');
      // Resolve the id through the shared lookup, which normalises the directory's
      // keys too. Indexing the map with the stripped name never matched a spaced
      // key ("TEENS 1"), so every multi-word CG reached the request with an
      // undefined id and failed on `.toString()`.
      const cgId = resolveCgId(this.formData.cgName, this.cgData);
      if (cgId === undefined) {
        throw new Error(`CG "${normalizedCgName}" is not in the directory`);
      }
      this.payload.cgName = normalizedCgName;
      this.payload.cgId = cgId;
      this.updatedPayload.cgName = normalizedCgName;
      this.updatedPayload.cgId = cgId;

      this.loading = true;
      this.cardTitle = 'Submitting Request...';

      try {
        await axios.post(
          process.env.VUE_APP_API_URL + '/v2/profile/cg-update-request',
          {
            cgId: String(cgId),
            cgName: normalizedCgName,
          },
          {
            headers: {
              Authorization: `Bearer ${this.token}`,
            },
          },
        );
      } catch (e) {
        this.loading = false;
        this.cardTitle = 'Which connect group (CG) do you belong to?';
        throw e; // Re-throw for handleSubmit to catch
      }

      this.loading = false;
      this.isRequested = !this.isRequested;
      this.fieldDisable = !this.fieldDisable;
      if (this.hasNoMember) {
        await this.showMoved();
        return;
      }
      this.requestText = 'Edit request';
      this.payload.cgUpdateRequestFormId = true;
      this.cardTitle = MEMBER_RECEIVED_TITLE;
    },
    // The API has already moved the claimed CG, which puts the sign-up in that CG
    // leader's match list. Refresh the profile (cgName, and Home's demography follow
    // it) and say so; a failed refresh still names the CG as the directory spells it.
    async showMoved() {
      try {
        await this.$store.dispatch('profile/forceFetchProfile');
      } catch (e) {
        console.error('Profile refresh after CG change failed:', e);
      }
      const cgName = this.$store.state.profile?.cgName || this.formData.cgName.trim().toUpperCase();
      this.updatedTitle = cgUpdatedTitle(cgName);
      this.cardTitle = this.updatedTitle;
      this.$emit('profileRefreshed');
    },
    onRequestCancel() {
      this.requestText = 'Request for an update';
      this.isRequested = false;
      this.fieldDisable = true;
      this.cardTitle = this.restingTitle(this.payload);
      this.updatedPayload = JSON.parse(JSON.stringify(this.payload));
      this.formData.cgName = this.payload.cgName || '';
      this.formKey++; // refresh Vueform UI state
    },
    hideFooter(val) {
      this.$emit('hideFooter', val);
    },
  },
  watch: {
    cgData: {
      handler() {
        // Recreate validator when cgData changes
        const self = this;
        this.invalidGroup = class extends Validator {
          get msg() {
            return ERROR_GROUP;
          }
          check(value) {
            return isResolvableCg(value, self.cgData);
          }
        };
        // Refresh form to pick up new validator
        this.$nextTick(() => {
          this.formKey++;
        });
      },
      immediate: true,
    },
    payload(newVal) {
      this.updatedPayload = JSON.parse(JSON.stringify(newVal));
      this.formData.cgName = newVal.cgName || '';
      this.cardTitle = this.restingTitle(newVal);
      // Ensure Vueform picks up new values
      this.$nextTick(() => {
        this.formKey++;
      });
    },
  },
};
</script>
<style scoped>
:deep(
  .locked-field input:disabled,
  .locked-field textarea:disabled,
  .locked-field select:disabled
) {
  background-color: #ecebeb !important;
  color: #7c7b78 !important;
  border-color: #ecebeb !important;
  -webkit-text-fill-color: #7c7b78; /* Chrome/Safari */
  opacity: 1; /* undo browser dimming */
  cursor: not-allowed;
}
</style>
